import { useNavigate } from 'react-router';

import { useUserEnvironmentActions } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import { addItemsToCart } from 'modules/orgDashboard/api/orgDashboardApiCart';
import { useOrgDashboardOrgContext } from 'modules/orgDashboard/contexts/OrgDashboardOrgContext';
import { useABExperimentOrgDashboardCartRoute } from 'modules/orgDashboard/hooks/useABExperimentOrgDashboardCartRoute';
import { orgDashboardListInvoicesRoute } from 'modules/orgDashboard/routing/orgDashboard.routes';
import { showModal } from 'zustand-stores/modalStore';

export const useHandleBuy = ({
  canRenew,
  setError,
}: {
  canRenew: boolean | null;
  setError: (message: string) => void;
}) => {
  const { i18n } = useI18n();
  const navigate = useNavigate();
  const { fetchUserEnvironment } = useUserEnvironmentActions();
  const { org, reloadOrg } = useOrgDashboardOrgContext();
  const orgDashboardCartPath = useABExperimentOrgDashboardCartRoute().with({
    orgId: org.id,
  });
  const orgDashboardInvoicesPath = orgDashboardListInvoicesRoute.with({
    orgId: org.id,
  });

  const handleBuy = () => {
    if (org.membershipStatus === 'IN_CART') {
      showModal('CONFIRMATION', {
        type: 'info',
        title: i18n('Complete Checkout'),
        buttonText: i18n('Go to Cart'),
        body: i18n(
          'You already have an annual membership for this organization in your cart. Finish your purchase to start enjoying your member benefits.',
        ),
        onConfirm: async () => Promise.resolve(navigate(orgDashboardCartPath)),
        qaId: 'go-to-cart-modal',
      });
      return;
    }
    if (org.membershipStatus === 'UNPAID') {
      showModal('CONFIRMATION', {
        type: 'info',
        title: i18n('Complete Payment'),
        buttonText: i18n('Go to Invoice'),
        body: i18n(
          'You already have an unpaid annual membership invoice for this organization. Please complete your payment to join and unlock your member benefits.',
        ),
        onConfirm: async () =>
          Promise.resolve(navigate(orgDashboardInvoicesPath)),
        qaId: 'go-to-invoice-modal',
      });
      return;
    }

    void addItemsToCart(org.id, [
      {
        id: canRenew ? 'ORG_MEMBERSHIP_RENEW' : 'ORG_MEMBERSHIP_BUY',
        type: 'MEMBERSHIP',
      },
    ])
      .then(async () => {
        await reloadOrg();
        await fetchUserEnvironment();
        void navigate(orgDashboardCartPath);
        return;
      })
      .catch(() => {
        setError(i18n('An error occurred adding the membership to your cart.'));
      });
  };

  return handleBuy;
};
